/**
 * odata_equipment_client（旧 OData 设备/监测器接口客户端）—— OFBiz 迁移版
 * ------------------------------------------------------------------
 * 原实现通过 oData.js 调 /bods.svc/ OData 接口（URL 拼 token 鉴权）。
 * 旧接口下线后统一返回 "token invalid."，导致前端误判登录失效。
 *
 * 处理策略（保持各方法签名 (params, callback, errorCallback) 不变）：
 *   A 类：有 OFBiz 新接口的 → 委托 src/app/server/ofbiz 适配层
 *   B 类：无新接口的 → 降级返回空数据 / 走 errorCallback
 *
 * 注意：equipment_action.js 核心 CRUD（createEquipment / createMonitor 等）
 * 已直接调用 ofbiz.*，本文件残留方法多为子功能（计数器/别名/告警条件/
 * MIB/任务计划/3D拓扑/远程桌面），大部分无新接口。
 */
import oData from './odataCompat';
import Store from '../script/store';
import Base64  from '../script/base64.js';
import Util from '../script/utils.js';
import * as ofbiz from './ofbiz';

var serviceAddress = Store.get("serviceUrl");
var tokenVerify = Store.get("tokenUrl");

// 降级辅助：无新接口时不调旧 /bods.svc/，直接回调空数据
function degrade(callback, data) {
  callback && callback(data);
}

// 解包 OFBiz 分页响应：{list:[...], total} → 数组；兼容直接返回数组
function extractList(data) {
  if (Array.isArray(data)) return data;
  return data && Array.isArray(data.list) ? data.list : [];
}

class odata_equipment_client {

  constructor(){
    if (serviceAddress) {
      this.setOdataConfig();
    }
  }

  setOdataConfig() {
    oData().config({
      endpoint: Store.get("serviceUrl"),
      autoFormat: false,
      version: 4,
      headers: [],
      isAsync: true,
      appending: [{name: 'token', value: Store.get('token')}]
    });
  }

  // ==================== A 类：委托 OFBiz ====================

  // 获取资源详情（A 类 → equipment/getById）
  getResourcesPropertyDataFromID(recid, callback, errorCallback) {
    ofbiz.getEquipmentById(recid).then(
      (data) => callback && callback({ results: [data] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 资源告警规则（B 类 → 降级，旧 AlarmConfig 实现已由 alarmRule/* 替代，但此方法无活跃调用）
  getResourcesAlarmConfig(callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取设备详情（A 类 → equipment/getById）
  getEquipment(equipmentId, callback, errorCallback) {
    ofbiz.getEquipmentById(equipmentId).then(
      (data) => callback && callback({ results: [data] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 获取所有资源类型（B 类 → 已由 ofbiz.getEquipmentTypes 替代，降级）
  getAllResources(callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取资源类型关系（B 类 → 降级）
  getResourceTypeRel(filter, callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 按 GBCode 获取资产（B 类 → 资产模块降级）
  getAssetByGb(id, callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取资源大类（B 类 → 已由 ofbiz.getEquipmentTypes 替代，降级）
  getResourceTypes(callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取资源子类（B 类 → 已由 ofbiz.getEquipmentTypes 替代，降级）
  getResources(RecId, callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取设备型号（B 类 → MIB 库无接口，交接文档第 5 节）
  getResourceEquipmentModel(filter, callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取组完整树数据（A 类 → monitor/tree）
  getGroupAllData(filter, callback, errorCallback){
    ofbiz.getMonitorTree({ groupId: filter && filter.RecId }).then(
      (data) => callback && callback(data),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 获取设备完整数据（A 类 → equipment/getById，含监测器由前端另行获取）
  getEquipmentAllData(filter, callback, errorCallback){
    ofbiz.getEquipmentById(filter && filter.RecId).then(
      (data) => callback && callback(data),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 获取设备可用的监测器类型（A 类 → monitor/types）
  getMonitors(equipmentType, callback, errorCallback){
    ofbiz.getMonitorTypes({ equipType: equipmentType }).then(
      (data) => callback && callback({ results: extractList(data) }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 获取 MIB（B 类 → MIB 库无接口，交接文档第 5 节）
  getMibs(vendor, resourceModel, callback, errorCallback){
    degrade(callback, { results: [] });
  }

  // 获取监测器返回值定义（A 类 → monitor/returnItems）
  getMonitorReturnValues(monitorType, callback, errorCallback){
    ofbiz.getMonitorReturnItems(monitorType).then(
      (data) => callback && callback({ results: extractList(data) }, monitorType),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 按监测器类型获取告警条件模板（A 类 → monitor/alarmConditions）
  getInitialAlarmConditionDataFromMonitorType(monitorType, callback, errorCallback) {
    ofbiz.getMonitorAlarmConditions(monitorType).then(
      (data) => callback && callback({ results: extractList(data) }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 按监测器 ID 获取实例级告警条件（B 类 → 实例级阈值无查询接口，交接文档 4.2）
  getAlarmConditionDataFromMonitorId(monitorId, callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 获取监测器详情（A 类 → monitor/getById）
  getMonitorsPropertyDataFromID(monitorId, callback, errorCallback) {
    ofbiz.getMonitorById(monitorId).then(
      (data) => callback && callback(data),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: '' }),
    );
  }

  // 获取监测器 EntryAlias（B 类 → 别名子功能无新接口）
  getMonitorsEntryAliasDataFromID(monitorId, callback, errorCallback) {
    degrade(callback, { results: [] });
  }

  // 添加监测器（A 类 → monitor/create）
  // 老字段映射：EquipmentId→parentFixedAssetId, Title→fixedAssetName, MonitorType→MONITOR_TYPE,
  // RefreshTime/MonitorRefresh→REFRESH_TIME, SpecialProperties→SPECIAL_PROPS, MonitorTimeOut→TIMEOUT。
  // 磁盘/网卡/进程等多选监测器（addMultiMonitorsProperty）与批量添加（batchAddMonitors）
  // 直接传老结构对象走本方法，必须在此适配，否则后端报 parentFixedAssetId 不能为空。
  addMonitorProperty(dateObjec, callback, errorCallback) {
    const d = dateObjec || {};
    const pick = (...vals) => {
      for (const v of vals) {
        if (v !== undefined && v !== null && v !== '') return v;
      }
      return '';
    };
    const createData = {
      parentFixedAssetId: pick(d.parentFixedAssetId, d.EquipmentId),
      fixedAssetName: pick(d.fixedAssetName, d.Title),
      MONITOR_TYPE: pick(d.MONITOR_TYPE, d.MonitorType),
      TARGET_HOST: pick(d.ServerAddress, d.serverAddress, d.hostname, d.host, d.ipAddress, d.ip),
      TARGET_PORT: pick(d.Port, d.port, d.TARGET_PORT),
      REFRESH_TIME: pick(d.MonitorRefresh, d.RefreshTime, d.REFRESH_TIME),
      REFRESH_UNIT: pick(d.RefreshUnit, d.REFRESH_UNIT, 'MINUTES'),
      TIMEOUT: pick(d.MonitorTimeOut, d.TIMEOUT, 30000),
      IS_DISABLED: (d.IsDisabled === 'Y' || d.IsDisabled === true || d.IsDisabled === 'true' || d.IsDisabled === '1' || d.IsDisabled === 1
                    || d.IsDisable === 'Y' || d.IsDisable === true || d.IsDisable === 'true' || d.IsDisable === '1' || d.IsDisable === 1) ? true : false,
      SPECIAL_PROPS: pick(d.SPECIAL_PROPS, d.SpecialProperties, d.SpecialProps),
      // 计数器监测器勾选的计数器名（逗号拼接，对应老 EccMonitorCount 多行）
      SAVE_NAMES: pick(d.SaveNames, d.saveNames),
      WARN_THRESHOLD: pick(d.WarnThreshold),
      ERROR_THRESHOLD: pick(d.ErrorThreshold),
      THRESHOLD_OP: pick(d.ThresholdOp),
    };
    ofbiz.createMonitor(createData).then(
      (data) => {
        // 兼容旧消费方读取 data.RecId
        const result = Object.assign({}, data, { RecId: (data && (data.monitorId || data.fixedAssetId)) || '' });
        callback && callback(result);
      },
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 编辑监测器（A 类 → monitor/update）
  updateMonitorProperty(dateObjec, callback, errorCallback) {
    const updateData = Object.assign({}, dateObjec);
    if (dateObjec.RecId) {
      updateData.monitorId = dateObjec.RecId;
      delete updateData.RecId;
    }
    ofbiz.updateMonitor(updateData).then(
      (data) => callback && callback({ results: data }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 删除监测器（A 类 → monitor/delete）
  deleteMonitorProperty(recid, callback, errorCallback) {
    ofbiz.deleteMonitor(recid).then(
      (data) => callback && callback({ results: data }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // ==================== B 类：实例级告警条件（4.2 无查询接口） ====================

  addMonitorAlarmCondition(dateObjec, callback, errorCallback) {
    // 实例级阈值写入由 monitor/create 和 monitor/update 承担（WARN/ERROR_THRESHOLD）
    degrade(callback, {});
  }

  updateMonitorAlarmCondition(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteMonitorAlarmCondition(recid, callback, errorCallback) {
    degrade(callback, {});
  }

  // ==================== B 类：计数器（无新接口） ====================

  addMonitorPropertyCounter(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteMonitorPropertyCounter(recid, callback, errorCallback) {
    degrade(callback, {});
  }

  getMySqlMonitorPropertyCounter(monitorId, callback, errorCallback) {
    // 新链路：监测器详情 getById 返回 saveNames（勾选计数器，逗号分隔，见 doc/06），
    // 适配成老 EccMonitorCount 行结构 [{RecId, monitorid, savename, showname}]
    ofbiz.getMonitorById(monitorId).then(res => {
      const vo = (res && res.data) || {};
      const saveNames = (vo.saveNames || '').split(',').map(s => s.trim()).filter(Boolean);
      callback && callback(saveNames.map((name, i) => ({
        RecId: monitorId + '_' + i,
        monitorid: monitorId,
        savename: name,
        showname: name
      })));
    }).catch(() => degrade(callback, []));
  }

  // ==================== B 类：EntryAlias（无新接口） ====================

  addMonitorPropertyEntryAlias(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteMonitorPropertyEntryAlias(recid, callback, errorCallback) {
    degrade(callback, {});
  }

  updateMonitorPropertyEntryAlias(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  // ==================== B 类：设备 CRUD（已由 ofbiz 直接调用，降级兜底） ====================

  addResourceProperty(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  updateResourcesProperty(dateObjec, callback, errorCallback) {
    degrade(callback, { results: {} });
  }

  // ==================== B 类：任务计划（4.2 无排程接口） ====================

  getTaskScheduler(callback, errorCallback) {
    degrade(callback, []);
  }

  addTaskScheduler(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  updateTaskScheduler(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteTaskScheduler(recid, callback, errorCallback) {
    degrade(callback, {});
  }

  // ==================== B 类：设备附属信息（空实现保留） ====================

  getEquipmentInfos(filter, callback, errorCallback) {
    // 原实现即为空
  }

  // ==================== B 类：3D 拓扑（无新接口，doc/20 2.9 有 topo/* 但结构差异大） ====================

  getNetworkNodeType(callback, errorCallback) {
    degrade(callback, []);
  }

  getNetWorkNode(callback, errorCallback) {
    degrade(callback, []);
  }

  getNodeType(monitorId, callback, errorCallback) {
    degrade(callback, []);
  }

  addNetworkNode(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteNetworkNode(recid, callback, errorCallback) {
    degrade(callback, 'success');
  }

  updateNodeType(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  updateNodeXYLocal(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  getNetworkNodeTypeRelation(callback, errorCallback) {
    degrade(callback, []);
  }

  getNetworkNodeTypeRelation1(params, callback, errorCallback) {
    degrade(callback, []);
  }

  addNetworkNodeTypeRelation(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  getNetworkRelation(callback, errorCallback) {
    degrade(callback, []);
  }

  getNetworkRelation1(monitorId, callback, errorCallback) {
    degrade(callback, []);
  }

  getNetworkRelation2(params, callback, errorCallback) {
    degrade(callback, []);
  }

  addNetworkRelation(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  updateNetworkRelation(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteNetworkRelation(params, callback, errorCallback) {
    degrade(callback, 'success');
  }

  deleteNetworkRelation2(monitorId, callback, errorCallback) {
    degrade(callback, 'success');
  }

  // ==================== B 类：远程桌面（无新接口） ====================

  getRemoteDeskList(callback, errorCallback) {
    degrade(callback, []);
  }

  addRemoteDesk(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  updateRemoteDesk(dateObjec, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteRemoteDesk(recid, callback, errorCallback) {
    degrade(callback, {});
  }

  // ==================== B 类：MIB 设备信息维护（第 5 节无接口） ====================

  getEquipmentInfoMaintenanceData(callback, errorCallback){
    degrade(callback, []);
  }

  addEquipmentMaintenanceInfo(params, callback, errorCallback) {
    degrade(callback, {});
  }

  updateEquipmentMaintenanceInfo(params, callback, errorCallback) {
    degrade(callback, {});
  }

  deleteEquipmentMaintenanceInfo(RecId, callback, errorCallback) {
    degrade(callback, {});
  }

}

const ODataEquipmentClient = new odata_equipment_client();
export default ODataEquipmentClient;
